import { appConfig } from '@common/config/app.config';
import store from '@website/store/web-store';
import { CommonRootState } from '@common/types/common-root-state-type';
import { UserState } from '@common-reducers/UserReducer';
import { SendPermission, BtgptModel, BtgptPermissions } from '../types/btgpt-types';

/** Default permissions when the caller doesn't specify any: allow everything
 *  freely (matches the previous 'allow-all' default). */
export const DEFAULT_PERMISSIONS: BtgptPermissions = {
  send: true,
  schedule: true,
  history: true,
  archive: true,
};

/**
 * The btgpt-only chunks on top of the shared AI stream contract
 * (`text` / `thinking` / `tool_call` / `done` / `error`, parsed by the ONE SSE
 * reader in `@common-components/ai-chat`). A `tool_call` WITHOUT `toolName`
 * means "reset the text"; `settings-picker` / `settings-changed` come from the
 * deterministic /model /permissions command layer. Board blocks come
 * only from a saved turn's `toolResults` (the live stream never carries results).
 */
export interface BTGPTStreamChunk {
  type: 'text' | 'thinking' | 'tool_call' | 'error' | 'done' | 'settings-picker' | 'settings-changed';
  content?: string;
  toolName?: string;
  error?: string;
  /** `error` chunks: 'pending' = the turn is still running and its answer will be saved. */
  code?: string;
  conversationId?: string;
  costUsd?: number;
  setting?: 'model' | 'permissions';
  current?: string;
  options?: string[];
  value?: string;
  permissions?: BtgptPermissions;
}

/** Per-request options threaded from the view into a btgpt send. */
export interface BTGPTStreamOptions {
  /** Granular per-capability permissions (send/schedule/history/archive).
   *  Defaults to all-true (agent acts freely) when omitted. */
  permissions?: BtgptPermissions;
  /** Which AI model btgpt should use to generate this response. */
  model?: BtgptModel;
}

/** A conversation row for the sidebar list. */
export interface BTGPTConversationSummary {
  _id: string;
  title: string | null;
  lastMessageAt?: string;
  updatedAt?: string;
  cumulativeCostUsd?: number;
  /** Set when this conversation is bridged to a WhatsApp "AI Conversation"
   *  group (the `@g.us` id). The sidebar renders a WhatsApp badge for it. */
  waChatId?: string;
}

/** One persisted turn message (from the full transcript). Mirrors the backend
 *  `chat-messages` rows written per turn. */
export interface BTGPTTranscriptMessage {
  _id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: { tool?: string; action?: string; args?: any }[];
  toolResults?: {
    tool?: string;
    action?: string;
    data?: any;
    isError?: boolean;
  }[];
  createdAt?: string;
  /** Task 7: files attached to a user turn — name + mime only, never a URL/id. */
  attachments?: { name: string; mime: string }[];
}

export class BTGPTServerClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = appConfig.API_ENDPOINT;
  }

  /** List the signed-in user's conversations, newest activity first (for the sidebar). */
  async listConversations(limit = 50): Promise<BTGPTConversationSummary[]> {
    const res = await fetch(`${this.baseUrl}/btgpt/conversations?$sort[lastMessageAt]=-1&$limit=${limit}`, {
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) return [];
    const body = await res.json();
    return (body?.data ?? body ?? []) as BTGPTConversationSummary[];
  }

  /** Load one conversation's full saved transcript (turns with tool traces). */
  async loadTranscript(conversationId: string): Promise<{ title: string | null; messages: BTGPTTranscriptMessage[] }> {
    const res = await fetch(`${this.baseUrl}/btgpt/conversations/${conversationId}/messages`, { headers: this.getAuthHeaders() });
    if (!res.ok) return { title: null, messages: [] };
    const body = await res.json();
    return {
      title: body?.title ?? null,
      messages: (body?.messages ?? []) as BTGPTTranscriptMessage[],
    };
  }

  /** Read a conversation's persisted settings (model / permissions / language) so
   *  the header controls hydrate on open. */
  async getConversationSettings(conversationId: string): Promise<{
    model: BtgptModel;
    permissions: BtgptPermissions;
    language: string | null;
  } | null> {
    const res = await fetch(`${this.baseUrl}/btgpt/conversations/${conversationId}/settings`, { headers: this.getAuthHeaders() });
    if (!res.ok) return null;
    return (await res.json()) as {
      model: BtgptModel;
      permissions: BtgptPermissions;
      language: string | null;
    };
  }

  /** Persist a settings change (dropdown edit or picker click). Only the
   *  provided fields are updated; the backend validates + returns the resolved
   *  settings. */
  async patchConversationSettings(
    conversationId: string,
    patch: {
      model?: BtgptModel;
      permissions?: BtgptPermissions;
      language?: string;
    }
  ): Promise<{
    model: BtgptModel;
    permissions: BtgptPermissions;
    language: string | null;
  } | null> {
    const res = await fetch(`${this.baseUrl}/btgpt/conversations/${conversationId}/settings`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(patch),
    });
    if (!res.ok) return null;
    return (await res.json()) as {
      model: BtgptModel;
      permissions: BtgptPermissions;
      language: string | null;
    };
  }

  /**
   * Stop a running turn on the server (the chat's Stop button). Best-effort: the
   * caller aborts its own SSE/fetch regardless of this call's outcome.
   * 200 `{ conversationId, status: 'stopped' }` a turn was running and is now
   * stopped; `{ status: 'idle' }` nothing was running (already finished, or a
   * repeat stop) — both read as success. 404 (unknown/foreign/malformed id) or
   * a network failure resolve false.
   */
  async stopConversation(conversationId: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/btgpt/conversations/${conversationId}/stop`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const state = store.getState() as CommonRootState;
    const userState: UserState = state.UserReducer;
    const supportedUserId = new URLSearchParams(window.location.search).get('supportedUserId');
    const supportedWorkSpaceId = new URLSearchParams(window.location.search).get('workspaceId');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (userState?.accessToken) headers.Authorization = `Bearer ${userState.accessToken}`;
    if (userState?.socketId) headers['Socket'] = userState.socketId;
    if (supportedUserId) headers['supportedUserId'] = supportedUserId;
    if (supportedWorkSpaceId) headers['supportedWorkSpaceId'] = supportedWorkSpaceId;
    return headers;
  }

  /**
   * Send a message to the btgpt agent; resolves with the SSE response, which
   * `useAiStream` reads. `conversationId` keeps a thread going (null starts a
   * new one); the resolved id arrives on the terminal `done` chunk.
   * `attachments` (Task 7): uploaded file ids (`UploadedAttachment.uploadId`)
   * from `POST ai-chat/uploads` — staged into the pod's `./attachments/`
   * workspace so the model can `Read` them directly, and also passable as
   * `upload_id` to the `files` tool's `identify`/`ingest_invoice` actions
   * (I3 fix) when the file needs filing, not just reading.
   */
  openStream(
    message: string,
    conversationId: string | null,
    options: BTGPTStreamOptions,
    signal: AbortSignal,
    attachments?: string[]
  ): Promise<Response> {
    const permissions: BtgptPermissions = options.permissions ?? DEFAULT_PERMISSIONS;
    // Legacy field, derived for backward compat with any caller still reading
    // it server-side. `permissions` is the primary field and wins when both are sent.
    const legacySendPermission: SendPermission = permissions.send === false ? 'ask-before-send' : 'allow-all';
    return fetch(`${this.baseUrl}/btgpt`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      signal,
      body: JSON.stringify({
        message,
        conversationId: conversationId ?? undefined,
        permissions,
        sendPermission: legacySendPermission,
        model: options.model ?? 'haiku',
        attachments: attachments && attachments.length > 0 ? attachments : undefined,
      }),
    });
  }
}
